import { useTheme } from "@mui/material";
import { BarChart } from "echarts/charts";
import { LegendComponent, TooltipComponent } from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import ReactEChartsCore from "echarts-for-react/lib/core";
import React, { useMemo, useState } from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";

import { Typography } from "@/Components/v2/atoms/Typography";
import { LabelType, useConfig } from "@/hooks/useConfig";
import BreakdownChartToolTip from "@/shared/components/Sections/BreakdownChart/BreakdownChartToolTip";
import {
  ChannelData,
  OnYAxisClickFunction,
  ROIBreakdownEnum,
  ViewToggle,
  YAxisClickParams,
} from "@/shared/types";
import { isInsufficientSampleSize } from "@/Utils/dataFilters";
import { Layer } from "@/Utils/hierarchy";

import { generateChartOptions } from "./chartOptionsGenerator";
import { useChartData } from "./useChartData";

echarts.use([BarChart, LegendComponent, TooltipComponent, CanvasRenderer]);

interface ROIChartProps {
  channelROI: Record<string, ChannelData>;
  insightROI: Record<string, ChannelData>;
  totalSpend: number;
  totalRevenue: number;
  chartLevel: ROIBreakdownEnum;
  chartView: ViewToggle;
  isAscending: boolean;
  filterInsufficientData: boolean;
  onYAxisClick?: OnYAxisClickFunction | undefined;
}

const ROIChart: React.FC<ROIChartProps> = ({
  channelROI,
  insightROI,
  totalSpend,
  totalRevenue,
  chartLevel,
  chartView,
  isAscending,
  filterInsufficientData,
  onYAxisClick,
}) => {
  const muiTheme = useTheme();
  const { config } = useConfig();
  const formatCurrency = config!.currency.formatCurrency;
  const [legendStates, setLegendStates] = useState<Record<string, boolean>>({});

  const label: LabelType = {
    brand: config!.getLayerLabel(Layer.Brand),
    category: config!.getLayerLabel(Layer.Category),
    product: config!.getLayerLabel(Layer.Product),
  };

  const { mappedData, channelTotals } = useChartData(
    channelROI,
    insightROI,
    chartView,
    chartLevel,
    isAscending,
    filterInsufficientData
  );

  const isInsufficientData = mappedData.length === 0;

  const renderTooltip = useMemo(() => {
    return (params: any) => {
      const tooltipRoot = document.createElement("div");
      const root = createRoot(tooltipRoot);

      flushSync(() => {
        root.render(
          <BreakdownChartToolTip
            mappedData={mappedData}
            params={params}
            chartLevel={chartLevel}
            channelTotals={channelTotals}
            totalAggregateRevenue={totalRevenue}
            totalAggregateSpend={totalSpend}
            label={label}
            disableToolTip={
              chartLevel === ROIBreakdownEnum.ROI &&
              isInsufficientSampleSize(mappedData[params[0].dataIndex]!)
            }
            formatCurrency={formatCurrency}
            config={config}
          />
        );
      });
      return tooltipRoot;
    };
  }, [mappedData, chartLevel, channelTotals, totalRevenue, totalSpend, label, formatCurrency]);

  const onEvents = useMemo(
    () => ({
      click: (params: YAxisClickParams) => {
        if (onYAxisClick && mappedData && params.componentType === "yAxis") {
          onYAxisClick(params, mappedData);
        }
      },
      legendselectchanged: (params: { selected: Record<string, boolean> }) => {
        setLegendStates(params.selected);
      },
    }),
    [onYAxisClick, mappedData]
  );

  if (isInsufficientData) {
    return (
      <Typography variant="body1">
        Sorry, there is insufficient data to display the chart.
      </Typography>
    );
  }

  const chartOptions = {
    mappedData,
    channelTotals,
    totalRevenue,
    totalSpend,
    chartLevel,
    muiTheme,
    label,
    config,
    renderTooltip,
    legendStates,
    ...(onYAxisClick && { onYAxisClick }),
  };

  const options = generateChartOptions(chartOptions);

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={options}
      notMerge={true}
      lazyUpdate={true}
      style={{ height: "48rem", marginBottom: "2rem" }}
      onEvents={onEvents}
    />
  );
};

export default ROIChart;
